// lib/socketMain.js Original Socket class for WebRTC/Ably communication
import * as Ably from 'ably'
import chalk from 'chalk'
import { throttle } from 'throttle-debounce'
import { selectOutputInput } from '~/lib/outputMapping/input'
import { serverNow } from '~/lib/serverClock'
import { serverSettingsFrom } from '~/lib/serverSettings'
import store from '~/store'
import api from '~/store/api'
import { selectSessionStatus } from '~/store/api/selectors'
import {
	bumpPCGeneration,
	setAblyState,
	setClientMode,
	setDCState,
	setPresence,
	setRTCState,
	setRunnerDevice,
	setSessionPresence,
} from '~/store/app'
import { selectAutomationOnWire, selectIsRemote, selectIsRunner } from '~/store/app/selectors'
import { setMapCursor } from '~/lib/map/cursor'
import { heldMap } from '~/lib/map/held'
import { setRemoteMap } from '~/store/maps'
import { selectOutputCard, selectShownGuides, selectShownMap } from '~/store/maps/selectors'
import { selectOutputBlack } from '~/store/outputMapping/selectors'
import { checkH264Support } from './codecCheck'
import { deviceClass, hasClientMode, offerDeviceType, resolveClientMode, savedClientMode } from './clientMode'
import { resetInboundParams } from './inboundParams'
import { noteRemoteActivity, setActivitySender } from './sessionActivity'
import { imageCaptured, showErrorDialog, updateSessionStatus } from '~/store/app/thunks'
import { selectLive } from '~/store/live/selectors'
import { buildWirePatch, holdLiveSync, releaseLiveSync } from '~/store/middleware/liveSync'
import { selectFPS, selectSettings } from '~/store/settings/selectors'
import {
	ABLY_API_KEY,
	ABLY_CLIENT_ID,
	ABLY_MESSAGES,
	AUDIO_BITRATE,
	CLIENT_MODE_OVERRIDE,
	CONNECTION_STATUS,
	CONTENT_HINT,
	DC_MESSAGES,
	DEGRADATION_PREFERENCE,
	DEVICE_NAME,
	DEVICE_TYPES,
	ENCODING_PRIORITY,
	IS_CAMERA,
	IS_DEV,
	ICE_RESTART,
	JITTER_BUFFER_TARGET_MS,
	MAX_BITRATE,
	MAX_FRAMERATE,
	PARAM_SEND_MS,
	MIN_BITRATE,
	OFFLINE,
	RECORDING_STATUS,
	RTC_DIRECTION,
	RUNNER_GRACE_MS,
	SCALE_RESOLUTION_DOWN_BY,
	SCTP_CAUSE_CODES,
	SESSION_PHASE,
	SESSION_STATUS,
	SIM,
	SIM_READY,
	SYNC_TEST,
} from './constants'
import { receiveSignal as receiveLinkSignal } from './desktopLink'
import handleDCMessage from './handleDCMessage'
import { mediaArrived } from './ownMedia'
import { ICE_RESTART_TIMEOUT_MS, iceRecoveryAction } from './iceRecovery'
import transformSDP from './sdp'

import { pushError } from '~/store/ui'

const logger = console.scope('socket')

// Presence data is `{ name, device_type }`; an older client entered a bare name string.
const PRESENCE_DATA = { name: DEVICE_NAME, device_type: deviceClass() }
const presenceEntry = ({ id, clientId, data, action }) => ({
	id,
	clientId,
	name: data?.name ?? data,
	device_type: data?.device_type ?? null,
	action,
})

// Ably ops on teardown paths are abandoned by design — the connection is going away and there is nothing to retry. Without a handler their rejection reaches window.onunhandledrejection, which reports it with no usable stack; this names the call instead.
const fireAndForget = (p, what) => p?.catch?.(err => logger.info(`${what} failed (ignored):`, err?.message))

// The constants are kbps; RTCRtpEncodingParameters takes bps.
const VIDEO_BITRATES = { maxBitrate: MAX_BITRATE * 1000, minBitrate: MIN_BITRATE * 1000 }

class Socket {
	constructor() {
		this.pc = null
		this.dc = null
		this._reconnectTimeout = null
		this._iceDisconnectedTimeout = null
		this._iceCheckingTimeout = null
		this._dcOpenTimeout = null // Timeout for data channel to open
		this._runnerGrace = null
		this._retries = 0
		this._reconnecting = false
		this._kicked = false
		this._negotiating = false
		this._initialNegotiationDone = false // Track if first negotiation completed
		this._pendingNegotiation = false // Track if renegotiation was deferred
		this._ably = null
		this._userChannel = null
		this._userChannelAttached = false
		this._sessionChannel = null
		this._sessionChannelAttached = false
		// An output window: ask the runner for the project's output mapping once the session channel attaches.
		this.wantsOutputMapping = false
		this._channelName = null
		this._userChannelName = null
		this.transceiver = null

		// Create throttled sendDC function
		this.debounceDC = throttle(PARAM_SEND_MS, this.sendDC.bind(this))

		// DC log suppression: buffer intermediate logs during continuous actions
		this._dcLogBuffer = null
		document.addEventListener('pointerup', () => this.flushDCLog())

		// A MISSING KEY IS A REASON NOT TO CONNECT, exactly like `offline`. Ably's constructor throws `No authentication options provided` on an undefined key, and it throws here — in the Socket constructor, before anything can catch it — so the whole module tree that builds a Socket dies. `VITE_ABLY_API_KEY` is absent on any checkout without it in `.env`, which turned a missing optional credential into a hard boot failure with an error naming Ably's internals rather than the missing variable.
		//
		// `_ably` staying null is an already-supported state, not a new one: both channel setters check it and bail (`Ably not initialized`), which is what `offline` has always relied on.
		if (!OFFLINE && !ABLY_API_KEY) logger.warn('No VITE_ABLY_API_KEY — running without Ably')
		if (!OFFLINE && ABLY_API_KEY) {
			logger.info('Initializing Ably...')
			this._ably = new Ably.Realtime({
				key: ABLY_API_KEY,
				clientId: ABLY_CLIENT_ID,
				echoMessages: false,
			})

			this._ably.connection.on(async change => {
				store.dispatch(setAblyState(change.current))
				if (change.current === CONNECTION_STATUS.CONNECTED) {
					logger.info('Ably connected')
					this._attachSessionChannel()
					this._attachUserChannel()
					if (change.previous === 'disconnected' || change.previous === 'suspended') {
						logger.info('Re-syncing session after reconnect from', change.previous)
						store.dispatch(api.endpoints.getActiveSession.initiate(undefined, { forceRefetch: true }))
					}
				} else if (change.current === CONNECTION_STATUS.DISCONNECTED) {
					logger.info('Ably disconnected')
				}
			})

			// Handle mobile app returning from background
			if (typeof document !== 'undefined') {
				document.addEventListener('visibilitychange', () => {
					if (document.visibilityState === 'visible' && this._ably) {
						store.dispatch(api.endpoints.getActiveSession.initiate(undefined, { forceRefetch: true }))

						const state = this._ably.connection.state
						if (state === 'connected') {
							this._ably.connection.ping().catch(() => {
								logger.warn('Ably ping failed after background, forcing reconnect')
								try {
									this._ably.connection.close()
								} catch {
									/* ignore transport teardown errors */
								}
								this._ably.connection.connect()
							})
						} else if (state !== 'connecting') {
							logger.info('App visible, forcing Ably reconnect from', state)
							this._ably.connection.connect()
						}
					}
				})
			}
		}
	}

	updatePresence = async data => {
		if (!data) data = await this._userChannel.presence.get()

		// Deduplicate by clientId - keep the entry matching current connection, or latest
		const presenceMap = new Map()
		for (const entry of data) {
			const existing = presenceMap.get(entry.clientId)
			// Prefer entry matching our connection, otherwise keep latest (last wins)
			if (!existing || entry.connectionId === this._ably?.connection?.id) {
				presenceMap.set(entry.clientId, entry)
			}
		}

		const presence = Array.from(presenceMap.values()).map(presenceEntry)
		store.dispatch(setPresence(presence))
		this.ensureClientMode(presence)
	}

	/** Decides a phone's mode once, from the first user presence (a desktop online → remote); `[]` when presence never comes. */
	ensureClientMode = presence => {
		if (!hasClientMode() || store.getState().app.client_mode != null) return
		const mode = resolveClientMode({
			override: CLIENT_MODE_OVERRIDE,
			saved: savedClientMode(),
			presence,
			me: ABLY_CLIENT_ID,
		})
		logger.info('client mode', mode)
		store.dispatch(setClientMode(mode))
	}

	updateSessionPresence = async () => {
		if (!this._sessionChannel) return
		try {
			const data = await this._sessionChannel.presence.get()
			const presenceMap = new Map()
			for (const entry of data) {
				const existing = presenceMap.get(entry.clientId)
				if (!existing || entry.connectionId === this._ably?.connection?.id) {
					presenceMap.set(entry.clientId, entry)
				}
			}
			store.dispatch(setSessionPresence(Array.from(presenceMap.values()).map(presenceEntry)))
		} catch (err) {
			logger.error('Failed to update session presence:', err)
		}
	}

	get sessionChannel() {
		return this._channelName
	}

	set sessionChannel(value) {
		if (value === this._channelName) return
		this._channelName = value

		logger.info('Ably session channel >', value)
		if (!this._ably) {
			logger.error('Ably not initialized')
			return
		}
		if (this._sessionChannel) {
			logger.info('Unsubscribing from previous session channel:', this._channelName)
			this._sessionChannel.unsubscribe()
			this._sessionChannel.presence.unsubscribe()
			if (IS_CAMERA) {
				if (this._ably?.connection?.state === 'connected' && this._sessionChannel.state === 'attached') {
					fireAndForget(this._sessionChannel.presence.leave(), 'session presence leave')
				}
			}
			store.dispatch(setSessionPresence([]))
			// Cancel any pending reconnect since session is intentionally changing
			clearTimeout(this._reconnectTimeout)
			this._reconnectTimeout = null
			this._reconnecting = false
			this.stopWebRTC()
			this._sessionChannel = null
			this._sessionChannelAttached = false
		}

		// null/undefined is the documented detach signal — the teardown above has just serviced it. An empty string is nobody's intent, so it stays an error.
		if (!value?.length) {
			if (value == null) logger.info('Ably session channel cleared')
			else logger.error('Ably channel name is empty string')
			return
		}

		this._sessionChannel = this._ably.channels.get(value)

		if (!this._sessionChannel) {
			logger.error('Ably channel failed to initialize')
			return
		}

		this._attachSessionChannel()
	}

	_attachSessionChannel = () => {
		if (!this._sessionChannel || this._sessionChannelAttached) return
		if (this._ably.connection.state !== 'connected') {
			logger.info('Ably not connected, deferring session channel attach')
			return
		}

		this._sessionChannel.subscribe(ABLY_MESSAGES.STATUS_UPDATE, async payload => {
			const data = JSON.parse(payload.data)
			logger.info('Ably message: status update >', data)
			store.dispatch(updateSessionStatus(data))
		})

		this._sessionChannel.subscribe(ABLY_MESSAGES.LAUNCHING, async payload => {
			const data = JSON.parse(payload.data)
			logger.info('Ably message: launching >', data)
			if (data.phase !== SESSION_PHASE.GPU_FOUND) return
			if (selectSessionStatus(store.getState()) !== SESSION_STATUS.LAUNCH_REQUESTED) return
			store.dispatch(updateSessionStatus({ ...data, status: SESSION_STATUS.LAUNCH_REQUESTED }))
		})

		this._sessionChannel.subscribe(ABLY_MESSAGES.ANSWER, async payload => {
			const data = JSON.parse(payload.data)
			if (data.name !== ABLY_CLIENT_ID) return

			const answerDebug = {
				timestamp: Date.now(),
				signalingState: this.pc?.signalingState,
				iceConnectionState: this.pc?.iceConnectionState,
				dcState: this.dc?.readyState,
				timeSinceLastOffer: this._lastOfferTime ? Date.now() - this._lastOfferTime : null,
			}
			logger.warn('ANSWER received', answerDebug)

			if (this.pc.signalingState === 'stable') {
				logger.warn('ANSWER ignored - connection already stable (duplicate answer)')
				return
			}

			try {
				await this.pc.setRemoteDescription(data)
				const { direction } = this.transceiver
				logger.info('Direction:', direction)
			} catch (error) {
				logger.error('Error setting remote description:', error.message)
			}
		})

		this._sessionChannel.subscribe(ABLY_MESSAGES.OFFER_ERROR, async payload => {
			const data = JSON.parse(payload.data)
			logger.error('Ably message: offer error >', data)

			store.dispatch(
				pushError({
					code: 'OFFER_ERROR',
					title: 'Connection Failed',
					message:
						'Unable to establish video connection. This may be a browser compatibility issue. Please try a different browser or device.',
				})
			)
			store.dispatch(showErrorDialog())
		})

		// TODO: use a middleware Ably echoes a publish back: the sender already holds what it sent, and an echo landing mid-edit would undo it.
		this._sessionChannel.subscribe(ABLY_MESSAGES.SYNC_ACTION, async ({ data, connectionId }) => {
			if (connectionId === this._ably?.connection?.id) return
			logger.info('Ably message: sync action >', data)
			store.dispatch(data)
		})

		this._sessionChannel.subscribe(ABLY_MESSAGES.USER_ACTIVITY, ({ connectionId }) => {
			if (connectionId !== this._ably?.connection?.id) noteRemoteActivity()
		})

		this._sessionChannel.subscribe(ABLY_MESSAGES.OUTPUT_MAPPING_REQUEST, () => this.sendOutputMapping())
		// A window with its own editor has its own cursor.
		this._sessionChannel.subscribe(ABLY_MESSAGES.MAP_CURSOR, ({ data, connectionId }) => {
			if (connectionId !== this._ably?.connection?.id && !store.getState().maps?.editing)
				setMapCursor(data ?? null)
		})
		if (this.wantsOutputMapping) this.sendToSession(ABLY_MESSAGES.OUTPUT_MAPPING_REQUEST)

		if (SIM)
			this._sessionChannel.subscribe(SIM_READY, () => {
				if (this.pc?.signalingState === 'have-local-offer') this.reconnect()
			})

		for (const name of [ABLY_MESSAGES.LINK_OFFER, ABLY_MESSAGES.LINK_ANSWER, ABLY_MESSAGES.LINK_ICE])
			this._sessionChannel.subscribe(name, ({ data }) => receiveLinkSignal(name, data))

		this._sessionChannel.presence.subscribe(ABLY_MESSAGES.PRESENCE_ENTER, () => this.updateSessionPresence())
		this._sessionChannel.presence.subscribe(ABLY_MESSAGES.PRESENCE_LEAVE, () => this.updateSessionPresence())
		this._sessionChannel.presence.subscribe(ABLY_MESSAGES.PRESENCE_UPDATE, () => this.updateSessionPresence())

		if (IS_CAMERA) {
			this._sessionChannel.presence.enter(PRESENCE_DATA).catch(err => {
				logger.error('Failed to enter session presence:', err)
			})
		}

		this._sessionChannelAttached = true
		this.updateSessionPresence()
	}

	_attachUserChannel = () => {
		if (!this._userChannel || this._userChannelAttached) return
		if (this._ably.connection.state !== 'connected') {
			logger.info('Ably not connected, deferring user channel attach')
			return
		}

		this._userChannel.presence
			.get()
			.then(members => {
				const isDuplicate = members.find(m => m.clientId === ABLY_CLIENT_ID)
				if (isDuplicate) {
					logger.warn(`Duplicate clientId '${ABLY_CLIENT_ID}'`)
				}
			})
			.catch(err => logger.error('Failed to get user presence:', err))

		this._userChannel.presence.subscribe(ABLY_MESSAGES.PRESENCE_ENTER, member => {
			if (member.clientId === ABLY_CLIENT_ID && member.connectionId !== this._ably.connection.id) {
				logger.info(`Detected stale presence entry for ${ABLY_CLIENT_ID}, will be cleaned up by Ably timeout`)
			}

			this.updatePresence()
		})

		this._userChannel.presence.subscribe(ABLY_MESSAGES.PRESENCE_LEAVE, () => {
			this.updatePresence()
		})

		this._userChannel.presence.subscribe(ABLY_MESSAGES.PRESENCE_UPDATE, member => {
			logger.info('Ably message: presence update >', member)
			this.updatePresence()
		})

		this._userChannel.presence.enter(PRESENCE_DATA).catch(err => {
			logger.error('Failed to enter user presence:', err)
		})
		this.updatePresence()

		// TODO: use a middleware
		this._userChannel.subscribe(ABLY_MESSAGES.INVALIDATE_API_TAGS, async ({ data }) => {
			logger.info('Ably message: invalidate API tags >', data)
			store.dispatch(api.util.invalidateTags(data))
			const hasSessionTag = data.some(t => t.type === 'Session')
			if (hasSessionTag) {
				store.dispatch(api.endpoints.getActiveSession.initiate(undefined, { forceRefetch: true }))
			}
		})

		this._userChannel.subscribe(ABLY_MESSAGES.RECORDING_STATUS, async ({ data: payload }) => {
			const data = JSON.parse(payload)
			logger.info('Ably message: recording status >', data)
			if (data.event === RECORDING_STATUS.ENDED) mediaArrived('recordings', data.rec_id)

			const cacheEntry = store.getState().api.queries['getRecordings(undefined)']
			if (!cacheEntry?.data) {
				store.dispatch(api.endpoints.getRecordings.initiate(undefined, { forceRefetch: true }))
				return
			}

			if (data.event === RECORDING_STATUS.MERGED) {
				logger.info('Recording merged, fetching updated recording data')
				store.dispatch(
					api.util.updateQueryData('getRecordings', undefined, d => {
						const index = d.findIndex(r => r.id === data.rec_id)
						if (index !== -1) {
							d[index].status = RECORDING_STATUS.MERGED
							d[index].mergeProgress = 1
						}
					})
				)
				store.dispatch(
					api.endpoints.getRecording.initiate({ sessionId: data.sessionId, recordingId: data.rec_id })
				)
			} else {
				store.dispatch(
					api.util.updateQueryData('getRecordings', undefined, d => {
						const index = d.findIndex(r => r.id === data.rec_id)
						if (index !== -1) {
							d[index].status = data.event
							if (data.event === RECORDING_STATUS.ENDED) {
								// serverNow(): every timestamp in the recordings cache must be on the SERVER clock (`start` comes from data.timestamp, and the merge-progress bands compare `end` against serverNow()). Stamping this with the raw local clock on a skewed machine would offset the merge ring. See ~/lib/serverClock.
								d[index].end = serverNow()
							}
						} else {
							d.push({
								id: data.rec_id,
								sessionId: data.sessionId,
								name: `Recording ${data.rec_id}`,
								start: new Date(data.timestamp).getTime(),
								end: null,
								duration: 0,
								thumbnail: null,
								size: 0,
								status: data.event,
							})
							d.sort((a, b) => (a.start < b.start ? 1 : -1))
						}
					})
				)
			}
		})

		this._userChannel.subscribe(ABLY_MESSAGES.IMAGE_STATUS, ({ data: payload }) => {
			const data = JSON.parse(payload)
			logger.info('Ably message: image status >', data)
			store.dispatch(imageCaptured(data.image))
		})

		this._userChannelAttached = true
	}

	get userChannel() {
		return this._userChannelName
	}

	set userChannel(value) {
		if (value === this._userChannelName) return
		logger.info('Ably user channel >', value)

		if (!this._ably) {
			logger.error('Ably not initialized')
			this._userChannelName = null
			return
		}

		if (this._userChannel) {
			this._userChannel.unsubscribe()
			if (this._ably?.connection?.state === 'connected' && this._userChannel.state === 'attached') {
				fireAndForget(this._userChannel.presence.leave(), 'user presence leave')
			}
			this._userChannel = null
			this._userChannelAttached = false
		}
		if (!value?.length) {
			this._userChannelName = null
			return
		}

		this._userChannel = this._ably.channels.get(value)
		if (!this._userChannel) {
			logger.error('Ably channel failed to initialize')
			this._userChannelName = null
			return
		}

		this._userChannelName = value
		this._attachUserChannel()
	}

	// WebRTC

	// `extra` adds fields to the offer message, e.g. `ice_restart: true` for a re-offer on the existing peer connection.
	publishOffer = (extra = {}) => {
		// DEBUG: Track when offers are published
		this._lastOfferTime = Date.now()
		this._offerCount = (this._offerCount || 0) + 1
		logger.warn('PUBLISH_OFFER', {
			timestamp: this._lastOfferTime,
			offerCount: this._offerCount,
			signalingState: this.pc?.signalingState,
			iceConnectionState: this.pc?.iceConnectionState,
			dcState: this.dc?.readyState,
		})
		const offer = this.pc.localDescription

		if (!this._sessionChannel) {
			logger.warn('Session channel not initialized, cannot publish offer')
			return
		}
		if (this._sessionChannel.state === 'failed' || this._sessionChannel.state === 'detached') {
			logger.warn(`Session channel ${this._sessionChannel.state}, cannot publish offer`)
			return
		}

		const s = store.getState()

		// Derive send_video from transceiver direction:
		// - sendrecv/recvonly: client wants to receive video (send_video: true tells server to send)
		// - sendonly: client only sends, doesn't receive (send_video: false) Falls back to show.output for routes that don't use transceiver direction
		const direction = this.transceiver?.direction
		const deviceType = offerDeviceType(s.app.client_mode)
		// A remote receives no video (R1).
		const sendVideo =
			deviceType !== DEVICE_TYPES.REMOTE && (direction ? direction.includes('recv') : s.ui.show.output)

		const payload = {
			name: ABLY_CLIENT_ID,
			type: offer.type,
			sdp: offer.sdp,
			send_video: sendVideo,
			device_type: deviceType,
			// speex_audio: new URLSearchParams(window.location.search).get('speex_audio') === '1',
			...extra,
		}
		logger.info('Payload:', payload)

		fireAndForget(this._sessionChannel.publish(ABLY_MESSAGES.OFFER, JSON.stringify(payload)), 'publish offer')
	}

	startWebRTC = async () => {
		// A phone's type rides the offer, so it never connects before its mode is decided (first user presence).
		if (hasClientMode() && store.getState().app.client_mode == null) {
			logger.info('Waiting for client mode before starting WebRTC')
			this._modeWait ??= new Promise(resolve => {
				const off = store.subscribe(() => {
					if (store.getState().app.client_mode == null) return
					off()
					resolve()
				})
			})
			await this._modeWait
			this._modeWait = null
			if (this.pc) return
		}
		logger.info(`Starting WebRTC...`)
		// The encoder list known first, so the peer connection never waits for it on the main thread.
		await checkH264Support()
		this.pc = new RTCPeerConnection({
			sdpSemantics: 'unified-plan',
			// iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }]
		})
		window.pc = this.pc

		this.pc.onicegatheringstatechange = () => {
			logger.info(`ICE Gathering state > ${chalk.cyanBright(this.pc.iceGatheringState)}`)
		}

		const currentPC = this.pc // Capture reference for event handler gating
		this.pc.oniceconnectionstatechange = () => {
			// Ignore events from stale PC (prevents race condition during reconnection)
			if (currentPC !== this.pc) return
			const s = this.pc.iceConnectionState
			const c = s === 'connected' ? chalk.greenBright : s === 'disconnected' ? chalk.redBright : chalk.white
			logger.info(`ICE ${c(s)}`)
			store.dispatch(setRTCState(s))

			// Handle different ICE states
			if (s === 'failed') {
				logger.info('ICE failed')
				clearTimeout(this._iceCheckingTimeout)
				clearTimeout(this._iceDisconnectedTimeout)
				clearTimeout(this._dcOpenTimeout)
				this.recoverIce()
			} else if (s === 'checking') {
				clearTimeout(this._iceCheckingTimeout)
				this._iceCheckingTimeout = setTimeout(() => {
					if (this.pc?.iceConnectionState === 'checking') {
						logger.info('ICE stuck in checking for 8s, reconnecting')
						this.reconnect()
					}
				}, 8000)
			} else if (s === 'disconnected') {
				clearTimeout(this._iceCheckingTimeout)
				clearTimeout(this._iceDisconnectedTimeout)
				this._iceDisconnectedTimeout = setTimeout(() => {
					if (this.pc?.iceConnectionState === 'disconnected') {
						logger.info('ICE still disconnected after 2s')
						this.recoverIce()
					}
				}, 2000)
			} else if (s === 'connected') {
				clearTimeout(this._iceCheckingTimeout)
				clearTimeout(this._iceDisconnectedTimeout)
				clearTimeout(this._iceRestartTimeout)
				this._iceRestarts = 0
				// Start timeout for data channel to open (if not already open) DC should open shortly after ICE connects (DTLS handshake)
				if (this.dc?.readyState !== 'open') {
					clearTimeout(this._dcOpenTimeout)
					this._dcOpenTimeout = setTimeout(() => {
						if (this.dc?.readyState !== 'open') {
							logger.warn(
								`Data channel stuck in '${this.dc?.readyState}' state after ICE connected, reconnecting`
							)
							this.reconnect()
						}
					}, 5000) // 5 seconds should be enough for DTLS handshake
				}
			}
		}

		this.pc.onsignalingstatechange = () => {
			logger.info(`Signaling state > ${chalk.cyanBright(this.pc.signalingState)}`)
		}

		this.pc.ontrack = async e => {
			logger.info('onTrack')
			const stream = e.streams[0]
			const track = stream?.getVideoTracks()[0]

			// Log initial track state
			if (track) {
				logger.info('Track state:', {
					id: track.id,
					readyState: track.readyState,
					muted: track.muted,
					enabled: track.enabled,
				})

				// A remote track ends only when we close the peer connection (stopWebRTC), so this is a teardown notice, never a fault on its own. Mute/unmute below are the ones worth watching for freezes.
				track.onended = () =>
					logger.info('Video track ended', { id: track.id, pcState: this.pc?.connectionState })
				track.onmute = () => {
					logger.warn('🔇 VIDEO TRACK MUTED', {
						id: track.id,
						iceState: this.pc?.iceConnectionState,
						dcState: this.dc?.readyState,
					})
				}
				track.onunmute = () => {
					logger.info('🔊 VIDEO TRACK UNMUTED', {
						id: track.id,
						iceState: this.pc?.iceConnectionState,
						dcState: this.dc?.readyState,
					})
				}
			}

			// Log transceiver direction
			const transceiver = e.transceiver
			logger.info('Transceiver direction:', transceiver?.direction)

			window.incoming_stream = stream
			window.dispatchEvent(new Event('incoming_stream'))
			// A panel showing the loopback keeps it.
			if (selectOutputInput(store.getState(), 'main') === 'source') return
			if (window.output_video) {
				logger.info('Setting output video source ---')
				window.output_video.srcObject = stream
			} else {
				logger.warn('Output video element not found')
			}
		}

		this.pc.onnegotiationneeded = async () => {
			// DEBUG: Log detailed state when negotiationneeded fires
			const debugState = {
				timestamp: Date.now(),
				initialNegotiationDone: this._initialNegotiationDone,
				negotiating: this._negotiating,
				signalingState: this.pc?.signalingState,
				iceConnectionState: this.pc?.iceConnectionState,
				dcState: this.dc?.readyState,
				isActive: store.getState().app.active_device === ABLY_CLIENT_ID,
				activeDevice: store.getState().app.active_device,
				lastReplaceTrackTime: this._lastReplaceTrackTime,
				timeSinceReplaceTrack: this._lastReplaceTrackTime ? Date.now() - this._lastReplaceTrackTime : null,
			}
			logger.warn('NEGOTIATION_NEEDED fired', debugState)

			// Guard: Don't start new negotiation if one is already in progress
			if (this._negotiating) {
				logger.info('Negotiation needed but already negotiating, skipping')
				return
			}

			// Guard: Don't negotiate if signaling state is not stable
			if (this.pc.signalingState !== 'stable') {
				logger.info(`Negotiation needed but signaling state is ${this.pc.signalingState}, skipping`)
				return
			}

			// Guard: After initial negotiation, don't renegotiate while DC is still connecting This prevents a race condition where renegotiation breaks the DC handshake
			if (this._initialNegotiationDone && this.dc?.readyState === 'connecting') {
				logger.info('Negotiation needed but DC is still connecting, deferring until DC opens')
				this._pendingNegotiation = true
				return
			}

			logger.warn('NEGOTIATION_NEEDED proceeding to create offer', {
				initialDone: this._initialNegotiationDone,
			})

			try {
				this._negotiating = true

				const offer = await this.pc.createOffer()
				offer.sdp = transformSDP(offer.sdp)
				await this.pc.setLocalDescription(offer)

				await this.iceGatheringComplete()

				this.publishOffer()
			} catch (error) {
				logger.error('Failed to create offer:', error)
			} finally {
				this._negotiating = false
				this._initialNegotiationDone = true
			}
		}

		// Camera and microphone share one stream id, so the receiver lip-syncs them (imagine_srv docs/IMPLEMENTATION_PLAN.md, decision D-C).
		const outgoing = new MediaStream()

		// Create transceiver with initial direction
		this.transceiver = this.pc.addTransceiver('video', {
			direction: RTC_DIRECTION,
			streams: [outgoing],
			sendEncodings: [
				{
					...VIDEO_BITRATES,
					maxFramerate: MAX_FRAMERATE,
					scaleResolutionDownBy: SCALE_RESOLUTION_DOWN_BY,
					degradationPreference: DEGRADATION_PREFERENCE,
					priority: ENCODING_PRIORITY,
				},
			],
		})

		// Create audio transceiver for microphone
		this.audioTransceiver = this.pc.addTransceiver('audio', {
			direction: RTC_DIRECTION,
			streams: [outgoing],
			sendEncodings: [{ maxBitrate: AUDIO_BITRATE }],
		})

		// Hold a little downlink video so jitter does not show as freezes. Not in every browser (Chrome 114+, Safari 18.4+).
		if ('jitterBufferTarget' in this.transceiver.receiver) {
			this.transceiver.receiver.jitterBufferTarget = JITTER_BUFFER_TARGET_MS
		}

		// Signal that a fresh audio sender now exists. The mic is attached by an effect in useBackgroundStream; without this bump the effect never re-runs on a reconnect and the new sender keeps track === null, producing a recording with no audio.
		store.dispatch(bumpPCGeneration())

		// Create data channel FIRST so it's included in the offer SDP Must be created before any negotiation happens
		this.createDataChannel()

		// Re-attach outgoing stream if it exists (for reconnections) — only if still active source
		if (window.outgoing_stream && store.getState().app.source_device === ABLY_CLIENT_ID) {
			await this.replaceTrack(window.outgoing_stream)
		}
	}

	iceGatheringComplete = () =>
		new Promise(resolve => {
			const pc = this.pc
			if (pc.iceGatheringState === 'complete') {
				resolve()
				return
			}
			const checkState = () => {
				if (pc.iceGatheringState === 'complete') {
					pc.removeEventListener('icegatheringstatechange', checkState)
					resolve()
				}
			}
			pc.addEventListener('icegatheringstatechange', checkState)
		})

	// ICE dropped (disconnected for 2 s, or failed): an ICE restart first, the full reconnect if that fails (src/lib/iceRecovery.js).
	recoverIce = () => {
		const action = iceRecoveryAction(this.pc?.iceConnectionState, {
			enabled: ICE_RESTART,
			restarts: this._iceRestarts ?? 0,
		})
		if (action === 'none') return
		// An offer already in flight cannot be replaced by a restart offer.
		if (action === 'reconnect' || this._negotiating || this.pc.signalingState !== 'stable') {
			logger.info('ICE recovery: reconnecting')
			this.reconnect()
			return
		}
		this.restartIce()
	}

	// Re-offers on the same peer connection with fresh ICE credentials. Transceivers and the data channel survive, so media resumes without a new session on the server.
	restartIce = async () => {
		const pc = this.pc
		this._iceRestarts = (this._iceRestarts ?? 0) + 1
		logger.info('ICE recovery: restarting ICE')
		clearTimeout(this._iceRestartTimeout)
		this._iceRestartTimeout = setTimeout(() => {
			if (pc !== this.pc) return
			const state = pc.iceConnectionState
			if (state !== 'connected' && state !== 'completed') {
				logger.info(`ICE restart did not recover (${state}) in ${ICE_RESTART_TIMEOUT_MS} ms, reconnecting`)
				this.reconnect()
			}
		}, ICE_RESTART_TIMEOUT_MS)

		try {
			this._negotiating = true
			const offer = await pc.createOffer({ iceRestart: true })
			offer.sdp = transformSDP(offer.sdp)
			await pc.setLocalDescription(offer)
			await this.iceGatheringComplete()
			if (pc !== this.pc) return
			this.publishOffer({ ice_restart: true })
		} catch (error) {
			logger.error('ICE restart failed, reconnecting:', error)
			if (pc === this.pc) this.reconnect()
		} finally {
			if (pc === this.pc) this._negotiating = false
		}
	}

	sendDC = (type, payload) => {
		if (this.dc?.readyState === 'open') {
			if (this._dcLogBuffer && this._dcLogBuffer.type === type) {
				this._dcLogBuffer.payload = payload
				this._dcLogBuffer.count++
			} else {
				this.flushDCLog()
				logger.info('send dc', { type, payload })
				this._dcLogBuffer = { type, payload, count: 0 }
			}
			this.dc.send(JSON.stringify({ type, payload }))
			return true
		}
		logger.warn(`Cannot send "${type}" to data channel - DC state: ${this.dc?.readyState || 'null'}`)
		return false
	}

	flushDCLog = () => {
		const buf = this._dcLogBuffer
		if (!buf || buf.count === 0) {
			this._dcLogBuffer = null
			return
		}
		logger.info(`send to data channel (final, ${buf.count} skipped)`, { type: buf.type, payload: buf.payload })
		this._dcLogBuffer = null
	}

	sendToSession = (name, data) => {
		if (!this._sessionChannel) {
			logger.warn('Session channel not initialized, dropping message:', name)
			return
		}
		if (this._sessionChannel.state === 'failed' || this._sessionChannel.state === 'detached') {
			logger.warn(`Session channel ${this._sessionChannel.state}, dropping message:`, name)
			return
		}
		// logger.info('send to session:', { name, data })
		fireAndForget(this._sessionChannel.publish({ name, data }), `publish to session "${name}"`)
	}

	/** The runner's shown map to every session client: an output window loads no project, so this is the only way it gets one. */
	sendOutputMapping = () => {
		const s = store.getState()
		if (selectIsRunner(s))
			this.sendToSession(
				ABLY_MESSAGES.SYNC_ACTION,
				setRemoteMap({
					map: heldMap() ?? selectShownMap(s),
					guides: selectShownGuides(s),
					card: selectOutputCard(s),
					black: selectOutputBlack(s, 'mirror'),
				})
			)
	}

	/** The runner's map editor mouse to every session client: `[x, y]` in output px, or null when it left the output. */
	sendMapCursor = at => {
		if (selectIsRunner(store.getState())) this.sendToSession(ABLY_MESSAGES.MAP_CURSOR, at)
	}

	sendToUser = (name, data) => {
		if (!this._userChannel || this._userChannel.state !== 'attached') {
			logger.warn('User channel not attached, dropping message:', name)
			return
		}
		logger.info('send to user:', { name, data })
		fireAndForget(this._userChannel.publish({ name, data }), `publish to user "${name}"`)
	}

	stopWebRTC = (resetReconnecting = true) => {
		if (!this.pc && !this.dc) {
			return
		}
		logger.info('Stopping WebRTC...')
		clearTimeout(this._reconnectTimeout)
		clearTimeout(this._iceCheckingTimeout)
		clearTimeout(this._iceDisconnectedTimeout)
		clearTimeout(this._dcOpenTimeout)
		clearTimeout(this._iceRestartTimeout)
		this._iceRestarts = 0
		this._reconnectTimeout = null
		this._iceCheckingTimeout = null
		this._iceDisconnectedTimeout = null
		this._dcOpenTimeout = null
		this._retries = 0
		// Reset negotiating flag - old PC is gone, new one starts fresh
		this._negotiating = false
		this._initialNegotiationDone = false
		this._pendingNegotiation = false
		// Only reset reconnecting flag if not in the middle of reconnection
		if (resetReconnecting) {
			this._reconnecting = false
		}
		if (this.pc) {
			this.pc.close()
			this.pc = null
			window.pc = null
			store.dispatch(setRTCState(CONNECTION_STATUS.CLOSED))
		}
		if (this.dc) {
			this.dc = null
			window.dc = null
			store.dispatch(setDCState('closed'))
			this._armRunnerGrace()
		}
		if (window.incoming_stream) {
			window.incoming_stream = null
		}
	}

	// The server holds a dropped runner's role for its grace; past that it has moved on, and so does this desktop's autoplay and automation.
	_armRunnerGrace = () => {
		const s = store.getState()
		if (this._runnerGrace || !selectAutomationOnWire(s) || !selectIsRunner(s)) return
		this._runnerGrace = setTimeout(() => {
			this._runnerGrace = null
			if (this.dc?.readyState !== 'open') store.dispatch(setRunnerDevice(null))
		}, RUNNER_GRACE_MS)
	}

	replaceTrack = async stream => {
		if (!this.pc) {
			logger.warn('replaceTrack called but peer connection is null')
			return
		}

		// DEBUG: Track when replaceTrack is called
		this._lastReplaceTrackTime = Date.now()
		logger.warn('REPLACE_TRACK called', {
			timestamp: this._lastReplaceTrackTime,
			signalingState: this.pc?.signalingState,
			iceConnectionState: this.pc?.iceConnectionState,
			dcState: this.dc?.readyState,
			isActive: store.getState().app.active_device === ABLY_CLIENT_ID,
			caller: new Error().stack?.split('\n')[2]?.trim(), // Get caller info
		})

		// Replace video track
		const videoSender = this.pc.getSenders().find(s => s.track?.kind === 'video' || !s.track)
		const videoTrack = stream.getVideoTracks()[0]

		if (videoSender && videoTrack) {
			// Diagnostic logging for Android debugging
			logger.info('Video track state before replaceTrack:', {
				trackId: videoTrack?.id,
				trackLabel: videoTrack?.label,
				trackKind: videoTrack?.kind,
				trackReadyState: videoTrack?.readyState,
				trackMuted: videoTrack?.muted,
				trackEnabled: videoTrack?.enabled,
				senderTrackId: videoSender?.track?.id,
				senderTrackKind: videoSender?.track?.kind,
			})

			videoTrack.contentHint = CONTENT_HINT
			await videoSender.replaceTrack(videoTrack)

			// Verify track was replaced
			logger.info('Video track state after replaceTrack:', {
				senderTrackId: videoSender?.track?.id,
				senderTrackReadyState: videoSender?.track?.readyState,
				senderTrackMuted: videoSender?.track?.muted,
				senderTrackEnabled: videoSender?.track?.enabled,
				trackMatches: videoSender?.track?.id === videoTrack?.id,
			})

			// Log and verify sender parameters after track replacement
			try {
				const params = videoSender.getParameters()
				// logger.info('Video sender parameters after replaceTrack:', JSON.stringify(params, null, 2))

				// Check if encodings have maxFramerate set
				if (params.encodings && params.encodings[0]) {
					const encoding = params.encodings[0]
					logger.info(
						`📊 Video encoding params: maxFramerate=${encoding.maxFramerate}, maxBitrate=${encoding.maxBitrate}, ` +
							`scaleResolutionDownBy=${encoding.scaleResolutionDownBy}`
					)

					// If maxFramerate is not set or too low, re-apply it
					if (!encoding.maxFramerate || encoding.maxFramerate < MAX_FRAMERATE) {
						logger.warn(
							`⚠️ maxFramerate not set or low (${encoding.maxFramerate}), re-applying ${MAX_FRAMERATE}`
						)
						Object.assign(params.encodings[0], { maxFramerate: MAX_FRAMERATE, ...VIDEO_BITRATES })
						await videoSender.setParameters(params)
						logger.info('✅ Re-applied video encoding parameters')
					}
				}
			} catch (e) {
				logger.warn('Could not get/set video sender parameters:', e.message)
			}
		}

		// Replace audio track
		if (!this.pc) {
			logger.warn('replaceTrack: pc became null before audio replacement')
			return
		}
		const audioSender = this.pc.getSenders().find(s => s.track?.kind === 'audio' || (!s.track && s !== videoSender))
		const audioTrack = stream.getAudioTracks()[0]

		if (audioSender && audioTrack) {
			logger.info('Replacing audio track:', {
				trackId: audioTrack?.id,
				trackLabel: audioTrack?.label,
				trackReadyState: audioTrack?.readyState,
			})
			await audioSender.replaceTrack(audioTrack)
			logger.info('✅ Audio track replaced')
		} else if (audioTrack && !audioSender) {
			logger.warn('Audio track available but no audio sender found')
		} else if (!audioTrack) {
			logger.info('No audio track in stream (microphone may be disabled)')
		}

		await this.requestKeyFrame()
	}

	clearVideoTrack = async () => {
		if (!this.pc) return
		const videoSender = this.pc.getSenders().find(s => s.track?.kind === 'video')
		if (videoSender) {
			await videoSender.replaceTrack(null)
			logger.info('Video track cleared (no longer active source)')
		}
	}

	requestKeyFrame = async () => {
		const videoSender = this.pc?.getSenders().find(s => s.track?.kind === 'video')
		if (!videoSender) return
		try {
			const params = videoSender.getParameters()
			const encodingOptions = params.encodings.map(() => ({ keyFrame: true }))
			await videoSender.setParameters(params, { encodingOptions })
			logger.info('🔑 Keyframe requested')
		} catch (e) {
			logger.warn('Failed to request keyframe:', e.message)
		}
	}

	setDirection = async direction => {
		logger.info('setDirection', direction, !!this.pc, !!this.transceiver)
		if (!this.pc || !this.transceiver) {
			logger.warn('setDirection', 'Peer connection or transceiver not initialized')
			return
		}

		// Check if direction actually changed
		const currentDirection = this.transceiver.direction
		if (currentDirection === direction) {
			logger.info(`setDirection: direction already ${direction}, skipping renegotiation`)
			return
		}

		try {
			// Set new direction
			this.transceiver.direction = direction

			// Wait for any in-progress negotiation to complete
			await new Promise(resolve => {
				const checkNegotiation = () => {
					if (!this._negotiating) resolve()
					else setTimeout(checkNegotiation, 100)
				}
				checkNegotiation()
			})

			// Explicitly trigger renegotiation to update SDP with new direction Browser may not auto-trigger onnegotiationneeded for direction changes
			logger.info(`Triggering renegotiation for direction change to ${direction}`)
			this.pc.dispatchEvent(new Event('negotiationneeded'))
		} catch (error) {
			logger.error('Failed to set transceiver direction:', error)
		}
	}

	setAudioEnabled = async (enabled, audioTrack = null) => {
		logger.info(`setAudioEnabled: ${enabled}`, { hasTrack: !!audioTrack })

		if (SYNC_TEST && enabled && audioTrack) {
			const { mixMicTrack, getSyncTestAudioStream } = await import('~/lib/syncTest')
			mixMicTrack(audioTrack)
			const mixedTrack = getSyncTestAudioStream()?.getAudioTracks()[0]
			const sender = this.audioTransceiver?.sender || this.pc?.getSenders().find(s => s.track?.kind === 'audio')
			if (mixedTrack && sender) {
				await sender.replaceTrack(mixedTrack)
			}
			logger.info('🎤 Sync test: mic mixed into destStream')
			return
		}

		const audioSender = this.audioTransceiver?.sender
		if (!audioSender) {
			logger.warn('setAudioEnabled: No audio transceiver/sender')
			return
		}
		if (!this.pc || this.pc.connectionState === 'closed') {
			logger.warn('setAudioEnabled: peer connection is closed')
			return
		}

		if (enabled && audioTrack) {
			await audioSender.replaceTrack(audioTrack)
			logger.info('🎤 Audio track attached')
		} else if (!enabled) {
			await audioSender.replaceTrack(null)
			logger.info('🔇 Audio track removed (zero bandwidth)')
		} else {
			logger.warn('setAudioEnabled: enabled=true but no audioTrack provided')
		}
	}

	// Transport: REST + DataChannel

	recoverFromBackground = () => {
		clearTimeout(this._reconnectTimeout)
		this._reconnectTimeout = null
		this._reconnecting = false

		if (!this.pc) {
			if (this._ably?.connection?.state === 'connected') {
				logger.info('Restarting WebRTC after background return (Ably ready)')
				this.startWebRTC()
				return null
			} else {
				logger.info('Waiting for Ably before restarting WebRTC...')
				const cb = () => {
					if (!this.pc) {
						logger.info('Ably reconnected, starting WebRTC after background return')
						this.startWebRTC()
					}
				}
				this._ably?.connection?.once('connected', cb)
				return () => this._ably?.connection?.off('connected', cb)
			}
		} else {
			const iceState = this.pc.iceConnectionState
			if (iceState === 'disconnected' || iceState === 'failed' || iceState === 'closed') {
				logger.info(`WebRTC in ${iceState} state after background, reconnecting`)
				this.reconnect()
			}
		}
		return null
	}

	reconnect = () => {
		if (this._kicked) {
			logger.info('Kicked — not reconnecting')
			return
		}
		// Prevent multiple simultaneous reconnection attempts
		if (this._reconnecting) {
			logger.warn('Reconnection already in progress, skipping')
			return
		}

		this._reconnecting = true
		clearTimeout(this._reconnectTimeout)

		// User presence stays through a WebRTC reconnect: it says who is online (Ably), and a phone resolving its mode reads a desktop there.

		// Clean up old connection before reconnecting (don't reset _reconnecting flag)
		this.stopWebRTC(false)

		let RECONNECT_INTERVAL = parseInt(Math.min(Math.max(2, Math.pow(this._retries, 1.5)), 60))
		if (this._retries === 5) RECONNECT_INTERVAL += 15
		else if (this._retries === 20) RECONNECT_INTERVAL += 30
		logger.info(`Reconnecting in ${RECONNECT_INTERVAL}s...`)

		this._reconnectTimeout = setTimeout(() => {
			this.startWebRTC().finally(() => {
				this._reconnecting = false
				// Re-enter in case Ably lost the entry meanwhile; an enter while present is an update.
				if (this._userChannel) {
					this._userChannel.presence
						.enter(PRESENCE_DATA)
						.then(() => {
							this.updatePresence()
						})
						.catch(error => {
							logger.error('Failed to re-enter presence after reconnect:', error)
						})
				}
			})
		}, RECONNECT_INTERVAL * 1000)
		this._retries++
	}

	createDataChannel = () => {
		if (!this.pc) {
			logger.error('Peer connection not initialized')
			return
		}

		this.dc = this.pc.createDataChannel('data')
		if (IS_DEV) window.dc = this.dc
		store.dispatch(setDCState('connecting'))

		this.dc.onopen = () => {
			logger.info('Data channel opened')
			clearTimeout(this._dcOpenTimeout)
			this._dcOpenTimeout = null
			this._retries = 0
			clearTimeout(this._runnerGrace)
			this._runnerGrace = null
			store.dispatch(setDCState('open'))

			// What changed while the DC was closed is merged against the join snapshot (`mergeSnapshot`).
			holdLiveSync()

			// Send target FPS to server; a remote writes nothing on its own (R12), and the setting is session-wide
			const fps = selectFPS(store.getState())
			if (fps && !selectIsRemote(store.getState())) this.sendDC(DC_MESSAGES.SET_TARGET_FPS, { fps })

			// Process any deferred negotiation now that DC is open
			if (this._pendingNegotiation) {
				logger.info('Processing deferred negotiation')
				this._pendingNegotiation = false
				// Trigger renegotiation by dispatching event
				this.pc?.dispatchEvent(new Event('negotiationneeded'))
			}
		}

		this.dc.onmessage = async e => {
			let type, payload
			try {
				;({ type, payload } = JSON.parse(e.data))
			} catch (error) {
				logger.error('Failed to parse DataChannel message:', e.data, error)
				return
			}

			// Handle first client scenario before using shared handler. A remote never answers (R12): it takes the server's defaults as the snapshot.
			if (type === 'parameters' && payload.is_default && !selectIsRemote(store.getState())) {
				logger.info("I am first client, setting server's initial parameters")
				const state = store.getState()
				const live = selectLive(state)
				// The full state as a patch against nothing — same function, same rules, so the snapshot and every later diff cannot disagree about shape. `server_settings` is added AFTER, not inside: it rides the same envelope but is not a wire param.
				const params = buildWirePatch({}, live)
				params.server_settings = serverSettingsFrom(selectSettings(state))
				this.sendDC(DC_MESSAGES.PARAMETERS, params)
				releaseLiveSync(live)
				return
			}

			// Use shared data channel message handler
			handleDCMessage(type, payload)
		}

		const currentDC = this.dc // Capture reference for event handler gating
		this.dc.onclose = () => {
			// Ignore events from stale DC (prevents race condition during reconnection)
			if (currentDC !== this.dc) return
			logger.warn('Data channel closed')
			resetInboundParams()
			store.dispatch(setDCState('closed'))
			this._armRunnerGrace()
			// Only attempt reconnection if we still have an active session channel Don't reconnect when session is intentionally killed
			if (this._sessionChannel && this._channelName) {
				this.reconnect()
			}
		}

		this.dc.onerror = ({ error }) => {
			// if (!retries)
			if (error.sctpCauseCode === 12) return
			logger.warn('Data channel error:', error.message)

			switch (error.errorDetail) {
				case 'sdp-syntax-error':
					logger.error('SDP syntax error in line', error.sdpLineNumber)
					break
				case 'idp-load-failure':
					logger.error('Identity provider load failure: HTTP error', error.httpRequestStatusCode)
					break
				case 'sctp-failure':
					if (error.sctpCauseCode < SCTP_CAUSE_CODES.length) {
						logger.error('SCTP failure:', error.sctpCauseCode, SCTP_CAUSE_CODES[error.sctpCauseCode])
					} else {
						logger.error('Unknown SCTP error')
					}
					break
				case 'dtls-failure':
					if (error.receivedAlert) {
						logger.error('Received DLTS failure alert:', error.receivedAlert)
					}
					if (error.sentAlert) {
						logger.error('Sent DLTS failure alert:', error.receivedAlert)
					}
					break
			}
		}
	}
}

const socket = new Socket()

setActivitySender(() => socket.sendToSession(ABLY_MESSAGES.USER_ACTIVITY))

// A closing tab sends the server nothing, so it waits out ICE consent (~30 s) before dropping the peer; closing here tells it at once. A bfcache'd page is not gone.
window.addEventListener('pagehide', e => {
	if (!e.persisted) socket.stopWebRTC()
})

// Handle hot module replacement - cleanup old connections before new socket is created
if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		logger.info('HMR: Cleaning up socket before hot reload')
		socket.stopWebRTC()
	})
}

export default socket
